import { useState } from 'react';
import Card from 'components/card';
import { MdSmartToy, MdSearch, MdHelp, MdSchedule, MdPerson } from 'react-icons/md';

interface AIConversation {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  type: 'question' | 'reminder' | 'onboarding';
  subject: string;
  lastMessage: string;
  timestamp: string;
  unread: boolean;
}

const LatteAIConversations = () => {
  const [conversations] = useState<AIConversation[]>([
    {
      id: '1',
      userId: 'u1',
      userName: 'John Doe',
      type: 'question',
      subject: 'How to schedule a meeting?',
      lastMessage: 'To schedule a meeting, please click the calendar icon in chat view with other user.',
      timestamp: '2025-01-09T10:30:00.000Z',
      unread: false
    },
    {
      id: '2',
      userId: 'u2',
      userName: 'Jane Smith',
      type: 'reminder',
      subject: 'Meeting in 1 hour',
      lastMessage: 'Hi Jane! Your mentorship session starts in 1 hour at 2:00 PM. Here\'s the meeting link: [Join Meeting]',
      timestamp: '2025-01-09T13:00:00.000Z',
      unread: true
    },
    {
      id: '3',
      userId: 'u3',
      userName: 'Mike Johnson',
      type: 'reminder',
      subject: 'Meeting tomorrow',
      lastMessage: 'Hi Mike! You have a mentorship session tomorrow at 10:00 AM. The meeting will be held via video call.',
      timestamp: '2025-01-09T08:00:00.000Z',
      unread: false
    },
    {
      id: '4',
      userId: 'u4',
      userName: 'Sarah Williams',
      type: 'onboarding',
      subject: 'Welcome to Latte!',
      lastMessage: 'Welcome Sarah! I\'m here to help you get started. Complete your profile as much as possible, we can find you the best mentor in this way.',
      timestamp: '2025-01-08T16:00:00.000Z',
      unread: false
    },
    {
      id: '5',
      userId: 'u5',
      userName: 'Robert Brown',
      type: 'question',
      subject: 'How to update my availability?',
      lastMessage: 'You can update your availability in conversation view button card.',
      timestamp: '2025-01-08T14:30:00.000Z',
      unread: false
    },
    {
      id: '6',
      userId: 'u6',
      userName: 'Emily Davis',
      type: 'question',
      subject: 'How to cancel a meeting?',
      lastMessage: 'To cancel a meeting, you can check existing meeting in conversation view button card. There will be a reschedule button beside.',
      timestamp: '2025-01-08T12:00:00.000Z',
      unread: false
    },
    {
      id: '7',
      userId: 'u7',
      userName: 'David Wilson',
      type: 'reminder',
      subject: 'Meeting in 1 hour',
      lastMessage: 'Hi David! Your mentorship session starts in 1 hour at 3:00 PM. Here\'s the meeting link: [Join Video Call]',
      timestamp: '2025-01-08T14:00:00.000Z',
      unread: false
    },
    {
      id: '8',
      userId: 'u8',
      userName: 'Lisa Anderson',
      type: 'question',
      subject: 'Where can I find meeting notes?',
      lastMessage: 'Meeting notes will be automatically sent to your email once finish processing.',
      timestamp: '2025-01-07T15:00:00.000Z',
      unread: false
    },
    {
      id: '9',
      userId: 'u9',
      userName: 'Tom Harris',
      type: 'reminder',
      subject: 'Meeting tomorrow',
      lastMessage: 'Hi Tom! You have a mentorship session tomorrow at 3:00 PM. The meeting will be conducted via video call.',
      timestamp: '2025-01-07T14:00:00.000Z',
      unread: false
    },
    {
      id: '10',
      userId: 'u10',
      userName: 'Emma Martinez',
      type: 'question',
      subject: 'How to share my screen during meetings?',
      lastMessage: 'We use Google Meet for video calls. Please check Google Meet\'s instructions [here].',
      timestamp: '2025-01-07T11:00:00.000Z',
      unread: false
    }
  ]);

  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState<AIConversation | null>(null);

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesFilter = filterType === 'all' || conv.type === filterType;
    const matchesSearch = searchQuery === '' || 
      conv.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get type icon
  const getTypeIcon = (type: AIConversation['type']) => {
    switch (type) {
      case 'question':
        return <MdHelp className="h-5 w-5 text-blue-500" />;
      case 'reminder':
        return <MdSchedule className="h-5 w-5 text-orange-500" />;
      case 'onboarding':
        return <MdPerson className="h-5 w-5 text-purple-500" />;
      default:
        return <MdSmartToy className="h-5 w-5 text-gray-500" />;
    }
  };


  // Format time
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="mt-3 flex h-full w-full gap-5">
      {/* Conversations List */}
      <div className="w-full lg:w-[40%]">
        <Card extra="w-full h-full p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10">
                <MdSmartToy className="h-6 w-6 text-brand-500" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                  Latte AI Assistant
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Automated help, reminders, and tips
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="mb-4 flex gap-2">
            {['all', 'question', 'reminder', 'onboarding'].map((filter) => (
              <button
                key={filter}
                onClick={() => setFilterType(filter)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  filterType === filter
                    ? 'bg-brand-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>

          {/* Conversations */}
          <div className="space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation)}
                className={`cursor-pointer rounded-lg border p-4 transition-all hover:bg-gray-50 dark:hover:bg-navy-800 ${
                  selectedConversation?.id === conversation.id
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/10'
                    : conversation.unread
                    ? 'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(conversation.type)}
                    <span className="font-semibold text-navy-700 dark:text-white">
                      {conversation.userName}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTime(conversation.timestamp)}
                  </span>
                </div>
                <p className="text-sm font-medium text-navy-700 dark:text-white mb-1">
                  {conversation.subject}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {conversation.lastMessage}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Conversation Detail */}
      <div className="w-full lg:w-[60%]">
        <Card extra="w-full h-full p-6">
          {selectedConversation ? (
            <div className="flex h-full flex-col">
              {/* Header */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    {getTypeIcon(selectedConversation.type)}
                  </div>
                  <div>
                    <h5 className="font-semibold text-navy-700 dark:text-white">
                      {selectedConversation.userName}
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedConversation.subject}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {/* User Message */}
                <div className="flex justify-end">
                  <div className="max-w-[70%] rounded-lg bg-brand-500 p-3 text-white">
                    <p className="text-sm">
                      {selectedConversation.type === 'question' && selectedConversation.subject}
                      {selectedConversation.type === 'reminder' && "Thanks for the reminder!"}
                      {selectedConversation.type === 'onboarding' && "I'm new here, can you help me get started?"}
                    </p>
                    <p className="mt-1 text-xs opacity-70">
                      {formatTime(selectedConversation.timestamp)}
                    </p>
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500/10 flex-shrink-0">
                    <MdSmartToy className="h-5 w-5 text-brand-500" />
                  </div>
                  <div className="max-w-[70%] rounded-lg bg-gray-100 dark:bg-gray-800 p-3">
                    <p className="text-sm text-navy-700 dark:text-white">
                      {selectedConversation.lastMessage}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Latte AI • {formatTime(selectedConversation.timestamp)}
                    </p>
                  </div>
                </div>

              </div>

            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <MdSmartToy className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Select a conversation to view details
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LatteAIConversations;